import React, { useEffect, useState, useMemo } from "react";
import { Button, Select, Tag, Avatar } from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  DownOutlined,
  UpOutlined,
  UserOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { GetAllTasksByCycleApi } from "../../api/tasks/GetAllTasksByCycleApi";
import CompleteTaskApi from "../../api/tasks/CompleteTaskApi";
import {
  openNotificationSuccess,
  openNotificationError,
} from "../../helpers/NotificationHelpers";
import PageLoader from "../sub/PageLoader";
import moment from "moment";
import ConfirmationModal from "../modal/ConfirmationModal";

const { Option } = Select;

// ─── Avatar + name/designation ────────────────────────────────────────────────
function OwnerAvatar({ imageUrl, name, designation, size = 32 }) {
  const initials = (name || "?")
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0].toUpperCase())
    .slice(0, 2)
    .join("");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Avatar
        src={imageUrl || undefined}
        size={size}
        style={{
          backgroundColor: imageUrl ? undefined : "#0f172a",
          fontWeight: 700,
          fontSize: size * 0.38,
          flexShrink: 0,
        }}
      >
        {!imageUrl && initials}
      </Avatar>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
          {name || "—"}
        </div>
        {designation && (
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{designation}</div>
        )}
      </div>
    </div>
  );
}

// ─── Column shell — same coloured bg/border as DependenciesTab ────────────────
function Column({ bg, border, dotColor, titleColor, title, count, tagColor, emptyIcon, emptyText, children }) {
  return (
    <div style={{ backgroundColor: bg, border: `1px solid ${border}`, borderRadius: 12, padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: dotColor }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: titleColor }}>{title}</span>
        </div>
        <Tag color={tagColor} style={{ margin: 0, fontWeight: 700 }}>{count}</Tag>
      </div>
      {count === 0 ? (
        <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8", fontSize: 12 }}>
          {emptyIcon}
          {emptyText}
        </div>
      ) : children}
    </div>
  );
}

// ─── Collapsible dep list inside Bottlenecks card ────────────────────────────
function OrgTaskCard({ task, taskMap }) {
  const [open, setOpen] = useState(false);

  let ids = [];
  try {
    ids = task.blockedTaskIds
      ? typeof task.blockedTaskIds === "string"
        ? JSON.parse(task.blockedTaskIds)
        : task.blockedTaskIds
      : [];
  } catch { ids = []; }

  const depTasks = ids
    .map((id) => {
      const dep = taskMap.get(id);
      if (!dep) return null;
      return {
        ...dep,
        ownerName: dep.positions?.users
          ? `${dep.positions.users.firstName || ""} ${dep.positions.users.lastName || ""}`.trim()
          : "—",
        ownerDesig:
          dep.positions?.designations?.abbreviation ||
          dep.positions?.designations?.name ||
          "",
        ownerImageUrl: dep.positions?.users?.imageUrl,
      };
    })
    .filter(Boolean);

  const ownerName = task.positions?.users
    ? `${task.positions.users.firstName || ""} ${task.positions.users.lastName || ""}`.trim()
    : "—";
  const ownerDesig =
    task.positions?.designations?.abbreviation ||
    task.positions?.designations?.name ||
    "";
  const ownerImageUrl = task.positions?.users?.imageUrl;

  // "Waiting on N leader/leaders"
  const leaderLabel = depTasks.length === 1 ? "leader" : "leaders";

  return (
    // on-hold task card stays within burnt orange theme
    <div style={{ backgroundColor: "#fff", border: "1px solid #fed7aa", borderRadius: 10, padding: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <OwnerAvatar imageUrl={ownerImageUrl} name={ownerName} designation={ownerDesig} />
        <span style={{ fontSize: 10, backgroundColor: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.3px", border: "1px solid #fde68a" }}>On Hold</span>
      </div>

      {/* on-hold task name — yellow tinted label */}
      <div style={{ fontSize: 14, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>{task.name}</div>
      {task.plan?.title && (
        <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Plan: {task.plan.title}</div>
      )}
      {task.taskEndDate && (
        <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Due: {moment(task.taskEndDate).format("DD MMM YYYY")}</div>
      )}
      {task.holdReason && (
        <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>Reason: {task.holdReason}</div>
      )}

      {depTasks.length > 0 && (
        <div style={{ borderTop: "1px solid #e0e7ef", paddingTop: 8, marginTop: 4 }}>
          <div
            onClick={() => setOpen((v) => !v)}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", userSelect: "none" }}
          >
            {/* warm text for the waiting-on toggle */}
            <span style={{ fontSize: 11, fontWeight: 600, color: "#9a3412" }}>
              Waiting on {depTasks.length} {leaderLabel} to finish
            </span>
            <span style={{ fontSize: 11, color: "#9a3412" }}>
              {open ? <UpOutlined /> : <DownOutlined />}
            </span>
          </div>

          {open && (
            <div style={{ marginTop: 8 }}>
              {depTasks.map((dep) => (
                <div
                  key={dep.id}
                  style={{
                    backgroundColor: dep.completionStatus ? "#f0fdf4" : "#fff7ed",
                    border: `1px solid ${dep.completionStatus ? "#86efac" : "#fed7aa"}`,
                    borderRadius: 8, padding: "10px 12px", marginBottom: 8
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <OwnerAvatar imageUrl={dep.ownerImageUrl} name={dep.ownerName} designation={dep.ownerDesig} size={28} />
                    {dep.completionStatus
                      ? <Tag color="success" style={{ fontSize: 10, margin: 0 }}>Completed</Tag>
                      : <Tag color="default" style={{ fontSize: 10, margin: 0, background: "#f1f5f9", border: "1px solid #cbd5e1", color: "#64748b" }}>Not completed</Tag>
                    }
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: dep.completionStatus ? "#166534" : "#9a3412", marginBottom: 2 }}>{dep.name}</div>
                  {dep.plan?.title && (
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>Plan: {dep.plan.title}</div>
                  )}
                  {dep.taskEndDate && (
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Due: {moment(dep.taskEndDate).format("DD MMM YYYY")}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Collapsible resolved group card ─────────────────────────────────────────
function ResolvedGroupCard({ group }) {
  const [open, setOpen] = useState(false);
  const { heldTask, deps } = group;

  const isCompleted = heldTask.completionStatus;
  const badgeLabel = isCompleted ? "Resolved" : "Released · Awaiting Completion";
  const badgeBg = isCompleted ? "#dcfce7" : "#fef9c3";
  const badgeColor = isCompleted ? "#166534" : "#92400e";
  const badgeBorder = isCompleted ? "#86efac" : "#fde68a";

  return (
    <div style={{ backgroundColor: "#fff", border: "1px solid #bbf7d0", borderRadius: 10, padding: 14, marginBottom: 12 }}>
      {/* Held task header */}
      <div style={{ fontWeight: 700, fontSize: 13, color: "#166534", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
        <CheckCircleOutlined style={{ color: "#16a34a" }} />
        <span>{heldTask.name}</span>
        <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, backgroundColor: badgeBg, color: badgeColor, fontWeight: 700, border: `1px solid ${badgeBorder}` }}>{badgeLabel}</span>
      </div>
      {heldTask.ownerName && <OwnerAvatar imageUrl={heldTask.ownerImageUrl} name={heldTask.ownerName} size={26} />}
      {heldTask.planTitle && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Plan: {heldTask.planTitle}</div>}
      {heldTask.updatedOn && (
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
          {isCompleted ? "Resolved" : "Released"} {moment(heldTask.updatedOn).format("MMM DD")} {heldTask.daysToResolve ? `· ${heldTask.daysToResolve}d` : ""}
        </div>
      )}

      {/* Collapsible dependency tasks */}
      {deps.length > 0 && (
        <div
          onClick={() => setOpen(!open)}
          style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 10, cursor: "pointer", fontSize: 11, fontWeight: 600, color: "#2563eb" }}
        >
          <CaretRightOutlined style={{ fontSize: 9, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} />
          {deps.length} dependency task{deps.length > 1 ? "s" : ""} resolved
        </div>
      )}

      {open && deps.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {deps.map((dep) => (
            <div key={dep.id} style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#166534" }}>{dep.name}</div>
                <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, backgroundColor: "#dbeafe", color: "#1e40af", fontWeight: 700, border: "1px solid #93c5fd" }}>Resolved</span>
              </div>
              {dep.ownerName && dep.ownerName !== "—" && (
                <OwnerAvatar imageUrl={dep.ownerImageUrl} name={dep.ownerName} size={22} />
              )}
              {dep.planTitle && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>Plan: {dep.planTitle}</div>}
              {dep.updatedOn && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>Resolved {moment(dep.updatedOn).format("MMM DD")}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DependencyOverview() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [orgTasksFilterUser, setOrgTasksFilterUser] = useState(null); // null = all users
  const [confirmModal, setConfirmModal] = useState({ visible: false, taskId: null });
  const perfCycleState = useSelector((state) => state.perfCycleState.value);

  const positionState = useSelector((state) => state.positionState.value);

  const yearOptions = useMemo(() => {
    const c = moment().year();
    return [c - 1, c, c + 1];
  }, []);

  const weekOptions = useMemo(() => {
    const opts = [{ value: null, label: "All weeks" }];
    const total = moment().year(selectedYear).isoWeeksInYear();
    for (let w = 1; w <= total; w++) {
      const start = moment().year(selectedYear).isoWeek(w).startOf("isoWeek").format("DD MMM");
      const end = moment().year(selectedYear).isoWeek(w).endOf("isoWeek").format("DD MMM");
      opts.push({ value: w, label: `W${w} (${start} – ${end})` });
    }
    return opts;
  }, [selectedYear]);

  useEffect(() => {
    if (perfCycleState?.id) fetchTasks();
  }, [perfCycleState]);

  async function fetchTasks() {
    setLoading(true);
    const res = await GetAllTasksByCycleApi(perfCycleState.id);
    if (res.success) setTasks(res.data || []);
    setLoading(false);
  }

  function handleComplete(taskId) {
    setConfirmModal({ visible: true, taskId });
  }

  async function confirmComplete() {
    const res = await CompleteTaskApi(confirmModal.taskId);
    if (res.success) {
      openNotificationSuccess("bottomRight", "Task marked completed");
      fetchTasks();
    } else {
      openNotificationError("bottomRight", "Couldn't complete task");
    }
  }

  const taskMap = useMemo(() => {
    const m = new Map();
    tasks.forEach((t) => m.set(t.id, t));
    return m;
  }, [tasks]);

  const onHoldTasks = useMemo(() => tasks.filter((t) => t.onHold), [tasks]);

  // Unique user options for the Org Tasks filter
  const orgTasksUserOptions = useMemo(() => {
    const seen = new Map();
    onHoldTasks.forEach((t) => {
      const posId = t.assignedTo || t.positionId;
      if (!posId || seen.has(posId)) return;
      const name = t.positions?.users
        ? `${t.positions.users.firstName || ""} ${t.positions.users.lastName || ""}`.trim()
        : null;
      const desig = t.positions?.designations?.abbreviation || t.positions?.designations?.name || "";
      const imageUrl = t.positions?.users?.imageUrl;
      if (name) seen.set(posId, { posId, name, desig, imageUrl });
    });
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [onHoldTasks]);

  // Filtered on-hold tasks for Org Tasks column
  const filteredOnHoldTasks = useMemo(() => {
    let result = onHoldTasks;

    // Week filter
    if (selectedWeek) {
      const weekStart = moment().year(selectedYear).isoWeek(selectedWeek).startOf("isoWeek");
      const weekEnd = moment().year(selectedYear).isoWeek(selectedWeek).endOf("isoWeek");
      result = result.filter(t => {
        const taskEnd = t.taskEndDate ? moment(t.taskEndDate) : null;
        const taskStart = t.taskStartDate ? moment(t.taskStartDate) : null;
        if (taskEnd && taskEnd.isBetween(weekStart, weekEnd, null, "[]")) return true;
        if (taskStart && taskStart.isBetween(weekStart, weekEnd, null, "[]")) return true;
        if (taskStart && taskEnd && taskStart.isBefore(weekStart) && taskEnd.isAfter(weekEnd)) return true;
        return false;
      });
    }

    // User filter
    if (orgTasksFilterUser) {
      result = result.filter(t => (t.assignedTo || t.positionId) === orgTasksFilterUser);
    }

    return result;
  }, [onHoldTasks, orgTasksFilterUser, selectedWeek, selectedYear]);

  // All task IDs that appear in any on-hold task's blockedTaskIds
  const allBlockedIds = useMemo(() => {
    const s = new Set();
    onHoldTasks.forEach((t) => {
      let ids = [];
      try { ids = t.blockedTaskIds ? (typeof t.blockedTaskIds === "string" ? JSON.parse(t.blockedTaskIds) : t.blockedTaskIds) : []; } catch { ids = []; }
      ids.forEach((id) => s.add(id));
    });
    return s;
  }, [onHoldTasks]);

  // ── COLUMN 1: I am blocking these leaders ─────────────────────────────────
  // Only MY tasks (assigned to my position) that are referenced in someone
  // else's blockedTaskIds — same logic as the personal Dependencies tab
  // Exclude completed tasks — they move to Resolved
  const iAmBlocking = useMemo(() => {
    const myPositionId = positionState?.id;
    return [...allBlockedIds].map((id) => {
      const myTask = taskMap.get(id);
      if (!myTask) return null;
      // Only include tasks assigned to the current user's position
      if (myTask.assignedTo !== myPositionId && myTask.positionId !== myPositionId) return null;
      // Exclude completed — they show in Resolved
      if (myTask.completionStatus) return null;
      // the on-hold task that is waiting for this task
      const waitingTask = onHoldTasks.find((h) => {
        let ids = [];
        try { ids = h.blockedTaskIds ? (typeof h.blockedTaskIds === "string" ? JSON.parse(h.blockedTaskIds) : h.blockedTaskIds) : []; } catch { ids = []; }
        return ids.includes(id);
      });
      const ownerName = myTask.positions?.users
        ? `${myTask.positions.users.firstName || ""} ${myTask.positions.users.lastName || ""}`.trim() : "—";
      const ownerDesig = myTask.positions?.designations?.abbreviation || myTask.positions?.designations?.name || "";
      const ownerImageUrl = myTask.positions?.users?.imageUrl;
      const blockedOwnerName = waitingTask?.positions?.users
        ? `${waitingTask.positions.users.firstName || ""} ${waitingTask.positions.users.lastName || ""}`.trim() : "—";
      const blockedOwnerDesig = waitingTask?.positions?.designations?.abbreviation || waitingTask?.positions?.designations?.name || "";
      const blockedOwnerImageUrl = waitingTask?.positions?.users?.imageUrl;
      const daysOnHold = waitingTask ? Math.max(1, Math.ceil((Date.now() - new Date(waitingTask.updatedOn || waitingTask.createdOn)) / 86400000)) : 0;
      return {
        ...myTask, ownerName, ownerDesig, ownerImageUrl,
        planTitle: myTask.plan?.title || "",
        blockedOwnerName, blockedOwnerDesig, blockedOwnerImageUrl,
        blockedTaskName: waitingTask?.name || "",
        daysOnHold,
      };
    }).filter(Boolean);
  }, [allBlockedIds, taskMap, onHoldTasks, positionState]);

  // ── COLUMN 2: I am waiting on these leaders ───────────────────────────────
  // Only MY on-hold tasks — then show the dep tasks those are waiting on.
  // Each entry is one dep task that I need to finish before my task can resume.
  // Exclude completed dep tasks — they move to Resolved
  const iAmWaitingOn = useMemo(() => {
    const myPositionId = positionState?.id;
    const result = [];
    onHoldTasks
      .filter((t) => t.assignedTo === myPositionId || t.positionId === myPositionId)
      .forEach((myOnHoldTask) => {
        let ids = [];
        try { ids = myOnHoldTask.blockedTaskIds ? (typeof myOnHoldTask.blockedTaskIds === "string" ? JSON.parse(myOnHoldTask.blockedTaskIds) : myOnHoldTask.blockedTaskIds) : []; } catch { ids = []; }
        ids.forEach((id) => {
          const dep = taskMap.get(id);
          if (!dep) return;
          // Exclude completed — they show in Resolved
          if (dep.completionStatus) return;
          const ownerName = dep.positions?.users
            ? `${dep.positions.users.firstName || ""} ${dep.positions.users.lastName || ""}`.trim() : "—";
          const ownerDesig = dep.positions?.designations?.abbreviation || dep.positions?.designations?.name || "";
          const ownerImageUrl = dep.positions?.users?.imageUrl;
          result.push({
            ...dep, ownerName, ownerDesig, ownerImageUrl,
            planTitle: dep.plan?.title || "",
            myOnHoldTaskName: myOnHoldTask.name,
          });
        });
      });
    return result;
  }, [onHoldTasks, taskMap, positionState]);

  // ── COLUMN 4: Resolved ────────────────────────────────────────────────────
  // Catches two cases:
  // 1. Tasks that were on-hold (had holdReason) and are now off hold — resolved in last 30 days
  // 2. Dependency tasks (referenced in any on-hold task's blockedTaskIds) that are now completed
  const resolved = useMemo(() => {
    const cutoff = moment().subtract(30, "days");

    // Case 1: previously on-hold tasks that are no longer on hold
    const wasOnHold = tasks
      .filter((t) => !t.onHold && t.holdReason && moment(t.updatedOn).isAfter(cutoff))
      .map((t) => {
        // Parse release info from holdReason if auto-released
        let resolveType = "manual";
        let originalHoldReason = t.holdReason || "";
        let releasedBy = null;
        let releasedByTaskName = null;

        if (t.holdReason && t.holdReason.startsWith("[RELEASED]")) {
          resolveType = "released";
          const parts = t.holdReason.replace("[RELEASED] ", "").split(" ||| ");
          originalHoldReason = parts[0] || "";
          releasedBy = parts[1] || null;
          // Extract task name from "Released by "TaskName""
          const match = releasedBy?.match(/Released by "(.+)"/);
          releasedByTaskName = match ? match[1] : null;
        }

        return {
          ...t,
          resolveType,
          holdReason: originalHoldReason,
          releasedBy,
          releasedByTaskName,
          ownerName: t.positions?.users
            ? `${t.positions.users.firstName || ""} ${t.positions.users.lastName || ""}`.trim() : "—",
          ownerImageUrl: t.positions?.users?.imageUrl,
          planTitle: t.plan?.title || "",
          daysToResolve: Math.max(0, Math.ceil((new Date(t.updatedOn) - new Date(t.createdOn)) / 86400000)),
        };
      });

    // Case 2: dependency tasks (blockers) that are now completed
    // Build reverse map from ALL tasks with blockedTaskIds (on-hold AND released)
    const blockerToHeldTasks = {};
    
    // From current on-hold tasks
    onHoldTasks.forEach((t) => {
      let ids = [];
      try { ids = t.blockedTaskIds ? (typeof t.blockedTaskIds === "string" ? JSON.parse(t.blockedTaskIds) : t.blockedTaskIds) : []; } catch { ids = []; }
      ids.forEach((id) => {
        if (!blockerToHeldTasks[id]) blockerToHeldTasks[id] = [];
        if (!blockerToHeldTasks[id].includes(t.name)) blockerToHeldTasks[id].push(t.name);
      });
    });

    // From released tasks (wasOnHold) — they still have blockedTaskIds preserved
    wasOnHold.forEach((t) => {
      let ids = [];
      try { ids = t.blockedTaskIds ? (typeof t.blockedTaskIds === "string" ? JSON.parse(t.blockedTaskIds) : t.blockedTaskIds) : []; } catch { ids = []; }
      ids.forEach((id) => {
        if (!blockerToHeldTasks[id]) blockerToHeldTasks[id] = [];
        if (!blockerToHeldTasks[id].includes(t.name)) blockerToHeldTasks[id].push(t.name);
      });
      // Legacy: if blockedTaskIds is empty, extract dep name from holdReason
      if (ids.length === 0 && t.releasedByTaskName) {
        // Find the task by name in taskMap
        for (const [tid, task] of taskMap.entries()) {
          if (task.name === t.releasedByTaskName) {
            if (!blockerToHeldTasks[tid]) blockerToHeldTasks[tid] = [];
            if (!blockerToHeldTasks[tid].includes(t.name)) blockerToHeldTasks[tid].push(t.name);
            break;
          }
        }
      }
    });

    // Get all dep IDs from the map
    const allDepIds = new Set(Object.keys(blockerToHeldTasks));

    const completedBlockers = [...allDepIds]
      .map((id) => taskMap.get(id))
      .filter((t) => t && (t.completionStatus || (t.holdReason && t.holdReason.startsWith && t.holdReason.startsWith("[RELEASED]"))) && moment(t.updatedOn).isAfter(cutoff))
      .filter((t) => !wasOnHold.find((r) => r.id === t.id)) // no dupes
      .map((t) => {
        const heldTaskNames = blockerToHeldTasks[t.id] || [];
        return {
          ...t,
          resolveType: "dependency",
          releasedHeldTask: heldTaskNames.length > 0 ? heldTaskNames[0] : null,
          releasedHeldTaskCount: heldTaskNames.length,
          ownerName: t.positions?.users
            ? `${t.positions.users.firstName || ""} ${t.positions.users.lastName || ""}`.trim() : "—",
          ownerImageUrl: t.positions?.users?.imageUrl,
          planTitle: t.plan?.title || "",
          daysToResolve: Math.max(0, Math.ceil((new Date(t.updatedOn) - new Date(t.createdOn)) / 86400000)),
        };
      });

    return [...wasOnHold, ...completedBlockers];
  }, [tasks, allBlockedIds, taskMap]);

  const weeksWithData = useMemo(() => {
    const s = new Set();
    onHoldTasks.forEach((t) => {
      const d = t.taskEndDate ? moment(t.taskEndDate) : null;
      const dStart = t.taskStartDate ? moment(t.taskStartDate) : null;
      if (d && d.year() === selectedYear) s.add(d.isoWeek());
      if (dStart && dStart.year() === selectedYear) s.add(dStart.isoWeek());
    });
    return s;
  }, [onHoldTasks, selectedYear]);

  if (loading) return <PageLoader />;

  return (
    <div style={{ maxWidth: 1400 }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: 0 }}>Dependency Overview</h2>
          <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>
            Org-wide dependency health for the current performance cycle
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Select value={selectedYear} onChange={(val) => { setSelectedYear(val); setSelectedWeek(null); }} size="small" style={{ width: 90 }}>
            {yearOptions.map((y) => <Option key={y} value={y}>{y}</Option>)}
          </Select>
          <Select value={selectedWeek} onChange={(val) => setSelectedWeek(val)} size="small" style={{ width: 220 }} showSearch optionFilterProp="label">
            {weekOptions.map((opt) => (
              <Option key={opt.value ?? "all"} value={opt.value} label={opt.label}>
                <span style={{ color: opt.value && weeksWithData.has(opt.value) ? "#0f172a" : "#94a3b8", fontWeight: opt.value && weeksWithData.has(opt.value) ? 700 : 400 }}>
                  {opt.label} {opt.value && weeksWithData.has(opt.value) ? "●" : ""}
                </span>
              </Option>
            ))}
          </Select>
          {iAmBlocking.length > 0 && (
            <div style={{ padding: "5px 14px", borderRadius: 6, backgroundColor: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: 12, fontWeight: 700 }}>
              {iAmBlocking.length} blocking others
            </div>
          )}
          {iAmWaitingOn.length > 0 && (
            <div style={{ padding: "5px 14px", borderRadius: 6, backgroundColor: "#fffbeb", border: "1px solid #fde68a", color: "#d97706", fontSize: 12, fontWeight: 700 }}>
              {iAmWaitingOn.length} pending
            </div>
          )}
        </div>
      </div>

      {/* ── 4-column layout ────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 18, alignItems: "flex-start" }}>

        {/* ── COL 1: I am blocking these leaders ── */}
        <Column
          bg="#fff5f5" border="#fed7d7" dotColor="#dc2626" titleColor="#991b1b"
          title="I am blocking these leaders" count={iAmBlocking.length} tagColor="red"
          emptyIcon={<CheckCircleOutlined style={{ fontSize: 24, marginBottom: 6, display: "block" }} />}
          emptyText="No tasks blocking others"
        >
          {iAmBlocking.map((bt) => (
            <div key={bt.id} style={{ backgroundColor: "#fff", border: "1px solid #fecaca", borderRadius: 10, padding: 14, marginBottom: 12 }}>
              {/* ── Leader being blocked — avatar + name + designation ── */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                {bt.blockedOwnerImageUrl ? (
                  <img src={bt.blockedOwnerImageUrl} alt={bt.blockedOwnerName}
                    style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "#0f172a",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                    {(bt.blockedOwnerName || "?").split(" ").filter(Boolean).map(n => n[0].toUpperCase()).slice(0, 2).join("")}
                  </div>
                )}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
                    {bt.blockedOwnerName}{bt.blockedOwnerDesig ? ` · ${bt.blockedOwnerDesig}` : ""}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>is being blocked by you</div>
                </div>
              </div>

              {/* ── Their on-hold task ── */}
              <div style={{ backgroundColor: "#fff5f5", borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#991b1b", marginBottom: 4 }}>Their on-hold task</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 2 }}>{bt.blockedTaskName}</div>
                {bt.planTitle && <div style={{ fontSize: 11, color: "#94a3b8" }}>Plan: {bt.planTitle}</div>}
              </div>

              {/* ── My task that's blocking them ── */}
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>Complete your task to unblock them:</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#dc2626", marginBottom: 2 }}>{bt.name}</div>
              {bt.taskEndDate && (
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>Due: {moment(bt.taskEndDate).format("DD MMM YYYY")}</div>
              )}
              {bt.completionStatus ? (
                <span style={{ fontSize: 11, backgroundColor: "#dcfce7", color: "#166534", padding: "3px 10px", borderRadius: 4, fontWeight: 700, border: "1px solid #86efac" }}>✓ Completed</span>
              ) : bt.onHold ? (
                <div>
                  <span style={{ fontSize: 11, backgroundColor: "#fef3c7", color: "#92400e", padding: "3px 10px", borderRadius: 4, fontWeight: 700, border: "1px solid #fde68a" }}>On Hold — cannot complete</span>
                  <div style={{ fontSize: 10, color: "#92400e", marginTop: 4 }}>This task is on hold waiting on other tasks. Resume it or wait for its dependencies to finish.</div>
                </div>
              ) : (
                <Button size="small" type="primary" onClick={() => handleComplete(bt.id)}
                  style={{ backgroundColor: "#059669", borderColor: "#059669", fontSize: 11, fontWeight: 600, borderRadius: 6 }}>
                  <CheckCircleOutlined /> Mark completed
                </Button>
              )}
            </div>
          ))}
        </Column>

        {/* ── COL 2: I am waiting on these leaders ── */}
        {/* Each card = one dependency task I am waiting on, with who owns it */}
        <Column
          bg="#fffaf0" border="#fde68a" dotColor="#d97706" titleColor="#92400e"
          title="I am waiting on these leaders" count={iAmWaitingOn.length} tagColor="orange"
          emptyIcon={<CheckCircleOutlined style={{ fontSize: 24, marginBottom: 6, display: "block" }} />}
          emptyText="Not waiting on anyone"
        >
          {iAmWaitingOn.map((dep, idx) => (
            <div key={`${dep.id}-${idx}`} style={{ backgroundColor: "#fff", border: "1px solid #fde68a", borderRadius: 10, padding: 14, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <OwnerAvatar imageUrl={dep.ownerImageUrl} name={dep.ownerName} designation={dep.ownerDesig} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>{dep.name}</div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Plan: {dep.planTitle}</div>
              {dep.taskEndDate && (
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Due: {moment(dep.taskEndDate).format("DD MMM YYYY")}</div>
              )}
              <div style={{ fontSize: 11, color: "#d97706", marginBottom: 10 }}>Blocking my task: "{dep.myOnHoldTaskName}"</div>
              {dep.completionStatus ? (
                <span style={{ fontSize: 11, backgroundColor: "#dcfce7", color: "#166534", padding: "3px 10px", borderRadius: 4, fontWeight: 700, border: "1px solid #86efac" }}>✓ Completed</span>
              ) : dep.onHold ? (
                <div>
                  <span style={{ fontSize: 11, backgroundColor: "#fef3c7", color: "#92400e", padding: "3px 10px", borderRadius: 4, fontWeight: 700, border: "1px solid #fde68a" }}>On Hold — cannot complete</span>
                  <div style={{ fontSize: 10, color: "#92400e", marginTop: 4 }}>Waiting on its own dependencies to finish first.</div>
                </div>
              ) : (
                <Button size="small" type="primary" onClick={() => handleComplete(dep.id)}
                  style={{ backgroundColor: "#059669", borderColor: "#059669", fontSize: 11, fontWeight: 600, borderRadius: 6 }}>
                  <CheckCircleOutlined /> Mark completed
                </Button>
              )}
            </div>
          ))}
        </Column>

        {/* ── COL 3: Bottlenecks — all on-hold in the cycle, deps collapsible ── */}
        <div style={{ backgroundColor: "#fff7ed", border: "1px solid #c2410c", borderRadius: 12, padding: 18 }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: "#c2410c" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#7c2d12" }}>Bottlenecks</span>
            </div>
            <Tag color="orange" style={{ margin: 0, fontWeight: 700, backgroundColor: "#ea580c", borderColor: "#c2410c", color: "#fff" }}>{filteredOnHoldTasks.length}</Tag>
          </div>

          {/* User filter */}
          <Select
            allowClear
            placeholder={
              <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                <UserOutlined /> Filter by user
              </span>
            }
            value={orgTasksFilterUser}
            onChange={(val) => setOrgTasksFilterUser(val ?? null)}
            size="small"
            style={{ width: "100%", marginBottom: 14 }}
            showSearch
            optionFilterProp="label"
            dropdownMatchSelectWidth={false}
          >
            {orgTasksUserOptions.map((u) => (
              <Option key={u.posId} value={u.posId} label={u.name}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Avatar
                    src={u.imageUrl || undefined}
                    size={22}
                    style={{ backgroundColor: u.imageUrl ? undefined : "#0f172a", fontSize: 9, fontWeight: 700, flexShrink: 0 }}
                  >
                    {!u.imageUrl && u.name.split(" ").filter(Boolean).map(n => n[0].toUpperCase()).slice(0, 2).join("")}
                  </Avatar>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{u.name}</div>
                    {u.desig && <div style={{ fontSize: 10, color: "#94a3b8" }}>{u.desig}</div>}
                  </div>
                </div>
              </Option>
            ))}
          </Select>

          {/* Cards */}
          {filteredOnHoldTasks.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#c2410c", fontSize: 12 }}>
              <CheckCircleOutlined style={{ fontSize: 24, marginBottom: 6, display: "block" }} />
              {orgTasksFilterUser ? "No on-hold tasks for this user" : "No tasks on hold in this performance cycle"}
            </div>          ) : (
            filteredOnHoldTasks.map((task) => (
              <OrgTaskCard key={task.id} task={task} taskMap={taskMap} />
            ))
          )}
        </div>

        {/* ── COL 4: Resolved ── */}
        <Column
          bg="#f0fff4" border="#86efac" dotColor="#16a34a" titleColor="#166534"
          title="Resolved" count={resolved.length} tagColor="green"
          emptyIcon={<WarningOutlined style={{ fontSize: 24, marginBottom: 6, display: "block" }} />}
          emptyText="No resolved dependencies in the last 30 days"
        >
          {(() => {
            // Group: held/released tasks as parents, their dependency tasks as children
            const groups = [];
            const usedDepIds = new Set();

            // First pass: collect released/resolved held tasks and attach their deps
            resolved.filter(t => t.resolveType === "released" || (t.resolveType === "manual" && t.holdReason)).forEach(heldTask => {
              // Find dependency tasks that belong to this held task
              const deps = resolved.filter(d => d.resolveType === "dependency" && d.releasedHeldTask === heldTask.name);
              deps.forEach(d => usedDepIds.add(d.id));
              groups.push({ heldTask, deps });
            });

            // Second pass: orphan dependency tasks (not linked to a visible held task)
            const orphanDeps = resolved.filter(t => t.resolveType === "dependency" && !usedDepIds.has(t.id));
            // Group orphans by their releasedHeldTask name
            const orphanGroups = {};
            orphanDeps.forEach(d => {
              const key = d.releasedHeldTask || "Unknown";
              if (!orphanGroups[key]) orphanGroups[key] = [];
              orphanGroups[key].push(d);
            });
            Object.entries(orphanGroups).forEach(([heldName, deps]) => {
              groups.push({ heldTask: { name: heldName, resolveType: "released", completionStatus: false, ownerName: null, ownerImageUrl: null, planTitle: deps[0]?.planTitle, updatedOn: deps[0]?.updatedOn, daysToResolve: null }, deps });
            });

            if (groups.length === 0 && resolved.length > 0) {
              // Fallback: just show flat
              return resolved.map(t => (
                <div key={t.id} style={{ backgroundColor: "#fff", border: "1px solid #bbf7d0", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#166534", display: "flex", alignItems: "center", gap: 6 }}>
                    <CheckCircleOutlined style={{ color: "#16a34a" }} />
                    <span>{t.name}</span>
                  </div>
                </div>
              ));
            }

            return groups.map((group, idx) => (
              <ResolvedGroupCard key={group.heldTask.id || group.heldTask.name || idx} group={group} taskMap={taskMap} />
            ));
          })()}
        </Column>

      </div>

      <ConfirmationModal
        visible={confirmModal.visible}
        title="Complete Task"
        text="Are you sure you want to mark this task as completed?"
        handleOk={confirmComplete}
        handleCancel={() => setConfirmModal({ visible: false, taskId: null })}
      />
    </div>
  );
}
